import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RankedPhotoCard from './components/RankedPhotoCard';
import ResultsSummaryPanel from './components/ResultsSummaryPanel';
import ComparisonModal from './components/ComparisonModal';
import ShareModal from './components/ShareModal';
import SocialLoginModal from './components/SocialLoginModal';
import HistoricalResultsLink from './components/HistoricalResultsLink';
import Button from '../../components/ui/Button';

import ConfirmDialog from '../../components/ui/ConfirmDialog';
import SuccessToast from '../../components/ui/SuccessToast';
import EmptyState from '../../components/ui/EmptyState';
import { generateSummaryData } from '../../services/photoAnalysisService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getSignedThumbnailUrl } from '../../utils/thumbnailCompression';
import BottomNav from '../../components/navigation/BottomNav';
import Header from '../../components/navigation/Header';

const RankingResultsDisplay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { creditBalance, analysisHistory, user, logout } = useAuth();

  const previousSessions = analysisHistory?.map(session => ({
    id: session?.id,
    photoCount: session?.photo_count,
    date: session?.created_at,
    averageScore: parseFloat(session?.average_score) || 0
  })) || [];

  const [showComparison, setShowComparison] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSocialLoginModal, setShowSocialLoginModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedPhotoForComparison, setSelectedPhotoForComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [showShareSuccess, setShowShareSuccess] = useState(false);

  const [rankedPhotos, setRankedPhotos] = useState(() => {
    const analysisResult = location?.state?.analysisResult;
    return analysisResult?.rankedPhotos || [];
  });

  const [hasAnalysisData, setHasAnalysisData] = useState(() => {
    return !!location?.state?.analysisResult;
  });

  const [summaryData, setSummaryData] = useState(() => {
    const analysisResult = location?.state?.analysisResult;
    if (analysisResult) {
      return generateSummaryData(analysisResult?.rankedPhotos, analysisResult?.summary);
    }
    return null;
  });

  useEffect(() => {
    const analysisResult = location?.state?.analysisResult;

    if (!analysisResult) {
      setIsLoading(false);
      setHasAnalysisData(false);
      return;
    }

    const resolveSignedUrls = async () => {
      const photos = analysisResult?.rankedPhotos || [];
      const resolved = await Promise.all(
        photos?.map(async (photo) => {
          if (photo?.thumbnailPath) {
            try {
              const signedUrl = await getSignedThumbnailUrl(supabase, photo?.thumbnailPath);
              return { ...photo, image: signedUrl };
            } catch (err) {
              console.warn('Failed to resolve signed URL for photo:', err?.message);
              return photo;
            }
          }
          return photo;
        })
      );

      // Deduplicate by id — guarantee every photo appears exactly once
      const seen = new Set();
      const deduplicated = resolved?.filter((photo) => {
        if (seen?.has(photo?.id)) return false;
        seen?.add(photo?.id);
        return true;
      });

      setRankedPhotos(deduplicated);
      setIsLoading(false);
      setHasAnalysisData(true);
    };

    resolveSignedUrls();
  }, [location?.state]);

  const handleDelete = (photoId) => {
    setPhotoToDelete(photoId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (photoToDelete) {
      setRankedPhotos((prev) => prev?.filter((p) => p?.id !== photoToDelete));
      setPhotoToDelete(null);
    }
  };

  const handleFavorite = (photoId) => {
    setRankedPhotos((prev) =>
      prev?.map((photo) =>
        photo?.id === photoId ? { ...photo, isFavorite: !photo?.isFavorite } : photo
      )
    );
  };

  const handleCompare = (photoId) => {
    setSelectedPhotoForComparison(photoId);
    setShowComparison(true);
  };

  const handleShare = (settings) => {
    if (settings?.platform && settings?.platform !== 'link') {
      setSelectedPlatform(settings?.platform);
      setShowShareModal(false);
      setShowSocialLoginModal(true);
    } else {
      setShowShareModal(false);
      setShowShareSuccess(true);
    }
  };

  const handleSocialLogin = () => {
    setShowSocialLoginModal(false);
    setShowShareSuccess(true);
  };

  const handleNewUpload = () => {
    navigate('/photo-upload-interface');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/pricing')}
        onLogout={logout}
      />
      <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {isLoading ? (
          <div className="space-y-6 sm:space-y-8 animate-pulse">
            {/* Page header skeleton */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 rounded w-48"></div>
                <div className="h-4 bg-gray-100 rounded w-56"></div>
              </div>
              <div className="flex gap-2">
                <div className="h-9 bg-gray-200 rounded-lg w-28"></div>
                <div className="h-9 bg-gray-200 rounded-lg w-28"></div>
              </div>
            </div>

            {/* Two-column layout skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="lg:col-span-2 flex flex-col gap-4">
                {[1, 2, 3]?.map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden">
                    <div className="flex gap-4 p-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                      <div className="w-24 h-24 bg-gray-200 rounded-xl flex-shrink-0"></div>
                      <div className="flex-1 space-y-3 py-1">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-100 rounded w-full"></div>
                        <div className="h-3 bg-gray-100 rounded w-5/6"></div>
                        <div className="flex gap-2 pt-1">
                          <div className="h-8 bg-gray-200 rounded flex-1"></div>
                          <div className="h-8 bg-gray-200 rounded flex-1"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lg:col-span-1 space-y-5">
                <div className="bg-white rounded-2xl shadow-md p-5 space-y-4">
                  <div className="h-5 bg-gray-200 rounded w-36"></div>
                  <div className="h-20 bg-gray-100 rounded-xl"></div>
                  <div className="space-y-2">
                    {[1, 2, 3]?.map((j) => (
                      <div key={j} className="flex justify-between items-center">
                        <div className="h-3 bg-gray-200 rounded w-24"></div>
                        <div className="h-3 bg-gray-200 rounded w-10"></div>
                      </div>
                    ))}
                  </div>
                  <div className="h-10 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            </div>
          </div>
        ) : rankedPhotos?.length === 0 ? (
          <EmptyState
            icon="ImageOff"
            headline="No Photos to Display"
            explanation="Upload photos to see your ranking results and detailed analysis"
            ctaText="Upload Photos"
            ctaIcon="Upload"
            onCtaClick={handleNewUpload}
          />
        ) : (
          <div className="space-y-6 sm:space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-1 sm:mb-2">Photo Rankings</h1>
                <p className="text-sm sm:text-base text-muted-foreground caption">
                  {hasAnalysisData ? 'Your photos ranked by AI analysis' : 'No analysis data available'}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowShareModal(true)}
                  iconName="Share2"
                  iconPosition="left"
                  className="text-xs sm:text-sm"
                >
                  Share Results
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleNewUpload}
                  iconName="Upload"
                  iconPosition="left"
                  className="text-xs sm:text-sm"
                >
                  New Upload
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="lg:col-span-2">
                <div className="flex flex-col gap-3 sm:gap-4">
                  {rankedPhotos?.map((photo) => (
                    <RankedPhotoCard
                      key={photo?.id}
                      photo={photo}
                      rank={photo?.rank}
                      onDelete={handleDelete}
                      onFavorite={handleFavorite}
                      onCompare={handleCompare}
                      onReanalyze={() => navigate('/photo-upload-interface', { state: { reanalyze: photo?.id } })}
                    />
                  ))}
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="lg:sticky lg:top-24 space-y-5 sm:space-y-6">
                  {summaryData && (
                    <ResultsSummaryPanel
                      summary={summaryData}
                      onShare={() => setShowShareModal(true)}
                      onNewUpload={handleNewUpload}
                    />
                  )}
                  {previousSessions?.length > 0 && (
                    <HistoricalResultsLink sessions={previousSessions} />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input for camera button */}
      <input
        id="camera-file-input-results"
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        multiple
      />

      <BottomNav activePath="/ranking-results-display" cameraInputId="camera-file-input-results" />

      {showComparison && (
        <ComparisonModal
          photos={rankedPhotos}
          selectedPhotoId={selectedPhotoForComparison}
          onClose={() => {
            setShowComparison(false);
            setSelectedPhotoForComparison(null);
          }}
        />
      )}
      {showShareModal && (
        <ShareModal
          onClose={() => setShowShareModal(false)}
          onShare={handleShare}
        />
      )}
      {showSocialLoginModal && (
        <SocialLoginModal
          platform={selectedPlatform}
          onClose={() => setShowSocialLoginModal(false)}
          onLogin={handleSocialLogin}
        />
      )}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPhotoToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Photo?"
        message="Are you sure you want to remove this photo from your results? This action cannot be undone."
        confirmText="Delete Photo"
        cancelText="Keep Photo"
        variant="danger"
      />
      <SuccessToast
        isOpen={showShareSuccess}
        onClose={() => setShowShareSuccess(false)}
        message="Results shared successfully!"
        duration={3000}
      />
    </div>
  );
};

export default RankingResultsDisplay;